import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { selCls, inputCls } from "../hooks/useMasterMaps";
import { toast } from "sonner";
import { CalendarPlus, Download, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { downloadBlob } from "../components/PipelineBits";

const MODES = ["In Person", "Video", "Telephonic"];
const EMPTY = { application_id: "", round_no: 1, panel_member_ids: [], interview_mode: "In Person", scheduled_on: "", venue: "" };

export default function Interviews() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [pending, setPending] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [apps, setApps] = useState([]);
  const [panelists, setPanelists] = useState([]);
  const [conflicts, setConflicts] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/interviews", { params: { pending_feedback: true } }),
                 api.get("/interviews", { params: { upcoming: true } })])
      .then(([p, u]) => { setPending(p.data); setUpcoming(u.data); })
      .catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    if (!can("pipeline", "write")) return;
    api.get("/pipeline").then(({ data }) => setApps(data.rows.filter(
      (r) => r.status === "Active" && ["Sourcing", "Screening", "Shortlisted", "Interview L1", "Interview L2"].includes(r.current_stage))));
    api.get("/panelists").then(({ data }) => setPanelists(data));
    const pre = params.get("app");
    if (pre) { setForm((f) => ({ ...f, application_id: pre })); setOpen(true); }
  }, [can, params]);

  const schedule = async (confirm = false) => {
    if (!form.application_id || !form.scheduled_on || form.panel_member_ids.length === 0) {
      toast.error("Application, date/time and at least one panel member are required"); return;
    }
    setBusy(true);
    try {
      const scheduled = new Date(form.scheduled_on).toISOString();
      await api.post("/interviews", { ...form, scheduled_on: scheduled, confirm_conflict: confirm });
      toast.success("Interview scheduled — panel notified with feedback link");
      setOpen(false); setForm(EMPTY); setConflicts(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.conflicts) setConflicts(d);
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const ics = async (iv) => {
    try {
      const { data } = await api.get(`/interviews/${iv.interview_id}/ics`, { responseType: "blob" });
      downloadBlob(data, `interview_${iv.interview_id}.ics`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const togglePanel = (id) => setForm((f) => ({
    ...f, panel_member_ids: f.panel_member_ids.includes(id)
      ? f.panel_member_ids.filter((p) => p !== id) : [...f.panel_member_ids, id]}));

  return (
    <div data-testid="interviews-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Interviews</h1>
          <p className="text-sm text-[#6B7280] mt-1">Feedback lives in the system — not on WhatsApp. Pendency counts against BUSINESS.</p>
        </div>
        {can("pipeline", "write") && (
          <button data-testid="interview-schedule-button" onClick={() => setOpen(true)}
            className="flex items-center gap-2 bg-[#0E2240] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200 self-start">
            <CalendarPlus size={15} /> Schedule Interview
          </button>
        )}
      </div>

      <h2 className="font-serif text-lg text-[#BB162B] mb-3">Feedback pending ({pending.length})</h2>
      {pending.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-sm p-6 text-sm text-[#6B7280] mb-8">No pending feedback — clean slate.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8" data-testid="pending-feedback-list">
          {pending.map((iv) => (
            <div key={iv.interview_id} data-testid={`pending-iv-${iv.interview_id}`} className="bg-white border border-[#BB162B]/50 rounded-sm p-4">
              <div className="flex justify-between items-start">
                <div className="text-sm font-medium text-[#0E2240]">{iv.candidate_name} · R{iv.round_no}</div>
                <span className="text-xs font-semibold text-[#BB162B] tabular-nums">{iv.feedback_pending_days}d overdue</span>
              </div>
              <div className="text-xs text-[#6B7280] mt-0.5">{iv.desig_name} · {iv.branch_name} · {iv.req_no}</div>
              <div className="text-[10px] text-[#6B7280] mt-1">Interviewed {String(iv.scheduled_on).slice(0, 16).replace("T", " ")} · Panel: {iv.panel_names.join(", ")}</div>
              <button data-testid={`open-feedback-${iv.interview_id}`} onClick={() => navigate(`/feedback/${iv.interview_id}`)}
                className="mt-3 w-full py-2 text-xs bg-[#BB162B] text-white rounded-sm hover:bg-[#a01324] transition-colors duration-200">
                Submit feedback now
              </button>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-serif text-lg text-[#0E2240] mb-3">Upcoming ({upcoming.length})</h2>
      {upcoming.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-sm p-6 text-sm text-[#6B7280]">Nothing scheduled.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="upcoming-list">
          {upcoming.map((iv) => (
            <div key={iv.interview_id} className="bg-white border border-gray-200 rounded-sm p-4">
              <div className="flex justify-between items-start">
                <div className="text-sm font-medium text-[#0E2240]">{iv.candidate_name} · R{iv.round_no}</div>
                <span className="text-[10px] uppercase text-[#6B7280]">{iv.interview_mode}</span>
              </div>
              <div className="text-xs text-[#6B7280] mt-0.5">{iv.desig_name} · {iv.branch_name}</div>
              <div className="text-xs text-[#0E2240] mt-1 tabular-nums">{String(iv.scheduled_on).slice(0, 16).replace("T", " ")}{iv.venue && ` · ${iv.venue}`}</div>
              <div className="text-[10px] text-[#6B7280] mt-1">Panel: {iv.panel_names.join(", ")}</div>
              <div className="mt-3 flex gap-2">
                <button data-testid={`ics-${iv.interview_id}`} onClick={() => ics(iv)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors duration-200">
                  <Download size={13} /> Calendar (.ics)
                </button>
                {iv.panel_member_ids.includes(user.user_id) && (
                  <button onClick={() => navigate(`/feedback/${iv.interview_id}`)}
                    className="flex-1 py-1.5 text-xs border border-[#1E5AA8] text-[#1E5AA8] rounded-sm hover:bg-[#1E5AA8]/5">Feedback form</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setConflicts(null); setForm(EMPTY); } }}>
        <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0E2240]">Schedule Interview</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">Panel members are notified with an in-system feedback link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <select data-testid="iv-application" className={inputCls} value={form.application_id}
              onChange={(e) => setForm((f) => ({ ...f, application_id: e.target.value }))}>
              <option value="">— Select candidate / application —</option>
              {apps.map((a) => <option key={a.application_id} value={a.application_id}>{`${a.candidate_name} — ${a.desig_name} (${a.req_no}) · ${a.current_stage}`}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="iv-round" className={inputCls} value={form.round_no} onChange={(e) => setForm((f) => ({ ...f, round_no: Number(e.target.value) }))}>
                <option value={1}>Round 1</option>
                <option value={2}>Round 2</option>
              </select>
              <select data-testid="iv-mode" className={inputCls} value={form.interview_mode} onChange={(e) => setForm((f) => ({ ...f, interview_mode: e.target.value }))}>
                {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <input data-testid="iv-datetime" type="datetime-local" className={inputCls} value={form.scheduled_on}
                onChange={(e) => setForm((f) => ({ ...f, scheduled_on: e.target.value }))} />
              <input data-testid="iv-venue" className={inputCls} placeholder="Venue / link" value={form.venue}
                onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))} />
            </div>
            <div>
              <div className="text-xs text-[#6B7280] mb-1.5">Panel members (availability is checked)</div>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-sm p-2 space-y-1" data-testid="iv-panel-list">
                {panelists.map((p) => (
                  <label key={p.user_id} className="flex items-center gap-2 text-xs text-[#0E2240]">
                    <input type="checkbox" data-testid={`panel-${p.user_id}`} checked={form.panel_member_ids.includes(p.user_id)}
                      onChange={() => togglePanel(p.user_id)} />
                    {p.full_name} <span className="text-[10px] text-[#6B7280]">({p.role.replace(/_/g, " ")})</span>
                  </label>
                ))}
              </div>
            </div>
            {conflicts && (
              <div data-testid="conflict-warning" className="border border-[#D97706]/50 bg-[#D97706]/5 rounded-sm p-3 text-xs">
                <div className="flex items-center gap-1.5 font-semibold text-[#D97706]"><AlertTriangle size={13} /> {conflicts.message}</div>
                <ul className="mt-1.5 text-[#6B7280] space-y-0.5">
                  {conflicts.conflicts.map((c) => (
                    <li key={c.interview_id}>{c.panel_names.join(", ")} — {String(c.scheduled_on).slice(0, 16).replace("T", " ")}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Cancel</button>
            {conflicts ? (
              <button data-testid="iv-schedule-anyway" disabled={busy} onClick={() => schedule(true)}
                className="px-4 py-2 text-sm bg-[#D97706] text-white rounded-sm hover:bg-[#b86505] disabled:opacity-50">Schedule anyway</button>
            ) : (
              <button data-testid="iv-schedule-confirm" disabled={busy} onClick={() => schedule(false)}
                className="px-4 py-2 text-sm bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] disabled:opacity-50">Schedule</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
